import React from 'react';

import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { FloatingLabelComponent } from 'design/floatingLabel';
import { TextInputControlComponent } from 'design/textInputControl';
import { TextComponent } from 'design/text/textComponents';
import { OrgInfoFormValues, OrgInfoFormViolation } from 'types/orgInfoTypes';
import { createOrgInfoFormViolationLense } from 'utils/validateOrgInfoFormValues';
import { CountryInputComponent } from 'containers/CountryInputComponent';

type Props = {
  values: OrgInfoFormValues;
  violations: OrgInfoFormViolation[];
  disabled: boolean;
  onNameChange(nextValue: string): void;
  onLocationChange(nextValue: string): void;
  onStaffCountChange(nextValue: string): void;
};

const ORG_NAME_MAX_LENGTH = 255;

export const ProdStackManagerOrgInfoFieldset = (props: Props) => {
  const {
    disabled,
    values,
    violations,
    onNameChange,
    onLocationChange,
  } = props;

  const {
    inputValidationRequired,
    prodStackManager: { clientInfoForm: ownDictionary },
  } = useIntlDictionary();

  const nameHint = React.useMemo(() => {
    for (let violation of violations) {
      switch (violation.name) {
        case 'NameMissed':
          return inputValidationRequired;
      }
    }
  }, [inputValidationRequired, violations]);

  const nameIsInvalid = violations.some((violation) =>
    createOrgInfoFormViolationLense(violation).isNameViolation()
  );

  const locationHint = React.useMemo(() => {
    for (let violation of violations) {
      switch (violation.name) {
        case 'LocationMissed':
          return inputValidationRequired;
      }
    }
  }, [inputValidationRequired, violations]);

  const locationIsInvalid = violations.some((violation) =>
    createOrgInfoFormViolationLense(violation).isLocationViolation()
  );

  return (
    <>
      <FloatingLabelComponent
        lifted={!!values.name}
        invalid={nameIsInvalid}
        titleText={
          <TextComponent color="secondary" size="m">
            {ownDictionary.nameInputLabelText}
          </TextComponent>
        }
        hintText={
          <TextComponent color="critical" size="s">
            {nameHint}
          </TextComponent>
        }
      >
        <TextInputControlComponent
          name="orgName"
          value={values.name}
          disabled={disabled}
          invalid={nameIsInvalid}
          maxLength={ORG_NAME_MAX_LENGTH}
          onValueChange={onNameChange}
        />
      </FloatingLabelComponent>
      <FloatingLabelComponent
        lifted={!!values.location}
        invalid={locationIsInvalid}
        titleText={
          <TextComponent color="secondary" size="m">
            {ownDictionary.locationInputLabelText}
          </TextComponent>
        }
        hintText={
          <TextComponent color="critical" size="s">
            {locationHint}
          </TextComponent>
        }
      >
        <CountryInputComponent
          name="orgLocation"
          value={values.location}
          invalid={locationIsInvalid}
          onChange={onLocationChange}
        />
      </FloatingLabelComponent>
    </>
  );
};
